import { Box, Flex } from '@chakra-ui/react';
import { Palette, ArrowLeft, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  HStack,
  Text,
} from '@ayble-health/dashboard-components';
import {
  PROTOTYPE_BAR_ENABLED,
  usePrototypeState,
} from '../PrototypeBar/PrototypeStateContext';

/**
 * Persistent workspace utility bar pinned to the top of the window.
 *
 * This is deliberately NOT part of the clinician (Care OS) experience —
 * it's a thin band above the whole app that carries workspace-level
 * tooling: a link to the design-system audit, and the toggle for the
 * prototype state controls (`PrototypeBar`, which expands directly below
 * this bar). Both entry points live here so they sit outside the clinical
 * SideNav.
 *
 * Rendered once in `Root`, above `AppLayout`, so it's present on every
 * route.
 */
export const GlobalBar = () => {
  const { pathname } = useLocation();
  const onDesignSystem = pathname.startsWith('/design-system');
  const { expanded, setExpanded, isDirty } = usePrototypeState();

  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      h="36px"
      flexShrink={0}
      bg="#02191C"
      px={4}
      borderBottomWidth="1px"
      borderColor="#0A2A2E"
    >
      <HStack gap={2} align="center">
        <Box
          w="8px"
          h="8px"
          rounded="full"
          bg="marigold"
          flexShrink={0}
          aria-hidden
        />
        <Text
          my={0}
          fontSize="xs"
          fontWeight={600}
          color="white"
          letterSpacing="0.02em"
        >
          Ayble Workspace
        </Text>
        <Text
          my={0}
          fontSize="2xs"
          fontWeight={600}
          color="bluegrey"
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          Product &amp; Design
        </Text>
      </HStack>

      <HStack gap={2} align="center">
        <RouterLink
          to={onDesignSystem ? '/provider-home' : '/design-system'}
          style={{ textDecoration: 'none' }}
        >
          <HStack
            gap={1.5}
            align="center"
            bg={onDesignSystem ? 'transparent' : 'rgba(255,255,255,0.08)'}
            px={2.5}
            py="4px"
            rounded="md"
            _hover={{ bg: 'rgba(255,255,255,0.16)' }}
            transition="background 0.12s ease"
          >
            {onDesignSystem ? (
              <ArrowLeft size={13} color="white" />
            ) : (
              <Palette size={13} color="#F7D432" />
            )}
            <Text my={0} fontSize="xs" fontWeight={600} color="white">
              {onDesignSystem ? 'Back to app' : 'Design system'}
            </Text>
          </HStack>
        </RouterLink>

        {/* Sits last, at the far right edge, so the control that opens the row
            below is the nearest thing to it — the usual accordion reading of
            header-then-panel. */}
        {PROTOTYPE_BAR_ENABLED && (
          <HStack
            as="button"
            // @ts-expect-error -- Chakra's `as` doesn't narrow to button props
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label="Toggle prototype state controls"
            gap={1.5}
            align="center"
            bg={expanded ? 'rgba(255,255,255,0.16)' : 'transparent'}
            borderWidth="1px"
            borderColor="rgba(255,255,255,0.14)"
            px={2.5}
            py="3px"
            rounded="md"
            _hover={{ bg: 'rgba(255,255,255,0.16)' }}
            transition="background 0.12s ease"
          >
            <SlidersHorizontal size={12} color="rgba(255,255,255,0.7)" />
            <Text my={0} fontSize="xs" fontWeight={600} color="white">
              Prototype
            </Text>
            {/* Collapsed, the bar gives no read on what's set — this dot flags
                that at least one control sits off its default, so a presenter
                isn't surprised by a scenario they left behind. */}
            {isDirty && !expanded && (
              <Box w="5px" h="5px" rounded="full" bg="marigold" flexShrink={0} aria-hidden />
            )}
            <Box
              transform={expanded ? 'rotate(180deg)' : 'none'}
              transition="transform 0.18s ease"
              lineHeight={0}
            >
              <ChevronDown size={13} color="rgba(255,255,255,0.5)" />
            </Box>
          </HStack>
        )}
      </HStack>
    </Flex>
  );
};
